<div class="ih">

  <div class="ih-toolbar">
    <label class="ih-search">
      <i class="fa fa-search ih-search-icon" aria-hidden="true"></i>
      <input type="text" class="ih-search-input" placeholder="Search imports"
             [value]="searchText" (input)="onSearchInput($any($event.target).value)" autocomplete="off" />
    </label>
    <button type="button" class="ih-refresh" (click)="refresh()" [disabled]="loading"
            title="Refresh" aria-label="Refresh import history">
      <img src="assets/img/refresh.svg" alt="" />
    </button>
  </div>

  <div class="ih-error" *ngIf="error">{{ error }}</div>

  <div class="ih-loading" *ngIf="loading && !runs.length">Loading import history&hellip;</div>

  <!-- Nothing imported AND nothing searched for: the genuine empty state. The one thing
       nobody would guess is that this is not only bank feeds, so it says so. -->
  <div class="ih-empty" *ngIf="!loading && !error && !runs.length && !searchText">
    <h3>No imports yet</h3>
    <p>
      Every time transactions come in &mdash; from a bank feed, from Xero, or from a
      spreadsheet you upload &mdash; the run is recorded here with what it imported and
      anything it skipped.
    </p>
  </div>

  <div class="ih-empty ih-empty--search" *ngIf="!loading && !error && !runs.length && searchText">
    <p>No imports match &ldquo;{{ searchText }}&rdquo;.</p>
  </div>

  <ul class="ih-list" *ngIf="runs.length" [class.ih-list--loading]="loading">
    <li class="ih-run" *ngFor="let run of runs; trackBy: trackById"
        [class.ih-run--open]="selectedId === run.id">

      <button type="button" class="ih-run-row" (click)="toggle(run)"
              [attr.aria-expanded]="selectedId === run.id">
        <span class="ih-run-main">
          <span class="ih-run-title">
            {{ sourceLabel(run) }}
            <span class="ih-run-target">&middot; {{ targetLabel(run) }}</span>
          </span>
          <span class="ih-run-meta">
            {{ localDate(run.startedUtc) | date: 'd MMM y, h:mm a' }}<span *ngIf="triggerLabel(run)"> &middot; {{ triggerLabel(run) }}</span>
          </span>
          <span class="ih-run-summary" [class.ih-run-summary--failed]="run.status === 'Failed'">
            {{ summary(run) }}
          </span>
        </span>
        <span class="ih-chip" [ngClass]="statusClass(run)">{{ run.status }}</span>
      </button>

      <!-- Opened in place: the counts, the period, and any rows that did not make it. -->
      <div class="ih-detail" *ngIf="selectedId === run.id">
        <dl class="ih-detail-grid">
          <div><dt>Requested</dt><dd>{{ run.requestedCount }}</dd></div>
          <div><dt>Imported</dt><dd>{{ run.insertedCount }}</dd></div>
          <div *ngIf="run.updatedCount"><dt>Updated</dt><dd>{{ run.updatedCount }}</dd></div>
          <div *ngIf="run.duplicateCount"><dt>Already here</dt><dd>{{ run.duplicateCount }}</dd></div>
          <div *ngIf="run.unmappedCount"><dt>No account</dt><dd>{{ run.unmappedCount }}</dd></div>
          <div *ngIf="run.voidedCount"><dt>Withdrawn</dt><dd>{{ run.voidedCount }}</dd></div>
          <div *ngIf="run.conflictCount"><dt>Already reconciled</dt><dd>{{ run.conflictCount }}</dd></div>
        </dl>

        <p class="ih-detail-period" *ngIf="run.periodFrom">
          Covering {{ localDate(run.periodFrom) | date: 'd MMM y' }}
          to {{ localDate(run.periodTo) | date: 'd MMM y' }}.
        </p>

        <!-- The one number worth a sentence: the bank and the books disagree about a row
             somebody has already signed off, and nothing was overwritten. -->
        <p class="ih-detail-warn" *ngIf="run.conflictCount">
          Your bank changed {{ run.conflictCount }}
          transaction{{ run.conflictCount === 1 ? '' : 's' }} that had already been
          reconciled or matched. We left {{ run.conflictCount === 1 ? 'it' : 'them' }}
          alone &mdash; check {{ run.conflictCount === 1 ? 'it' : 'them' }} against your statement.
        </p>

        <p class="ih-detail-error" *ngIf="run.errorMessage && run.status !== 'Failed'">{{ run.errorMessage }}</p>

        <!-- The transactions themselves: what was imported, changed or withdrawn, and where
             each one stands now. -->
        <div class="ih-txns" *ngIf="run.status !== 'Running' && run.status !== 'Failed'">
          <h4>
            Transactions
            <span class="ih-txns-count" *ngIf="selectedTransactionTotal">{{ selectedTransactionTotal }}</span>
          </h4>

          <p class="ih-muted" *ngIf="transactionsLoading">Loading transactions&hellip;</p>
          <p class="ih-detail-error" *ngIf="transactionsError">{{ transactionsError }}</p>
          <p class="ih-muted" *ngIf="recordedBefore(run)">
            Transactions are listed for imports from 19 Oct 2026 on. This one has its counts only.
          </p>
          <p class="ih-muted" *ngIf="!transactionsLoading && !transactionsError && !selectedTransactions.length && !recordedBefore(run)">
            Nothing was imported or changed.
          </p>

          <div class="ih-txn-row" *ngFor="let tx of selectedTransactions; trackBy: trackByTransaction">
            <span class="ih-txn-main">
              <span class="ih-txn-desc" [title]="tx.description">{{ tx.description || '—' }}</span>
              <span class="ih-txn-meta">
                {{ txDate(tx.transactionDate) | date: 'd MMM y' }}<span *ngIf="tx.accountName"> &middot; {{ tx.accountName }}</span><span *ngIf="nowLabel(tx)"> &middot; {{ nowLabel(tx) }}</span>
              </span>
            </span>
            <span class="ih-txn-side">
              <span class="ih-txn-amount">{{ tx.amount | number: '1.2-2' }}</span>
              <span class="ih-txn-action" [ngClass]="'ih-txn-action--' + (tx.action || '').toLowerCase()"
                    [title]="actionTitle(tx)">{{ actionLabel(tx) }}</span>
            </span>
          </div>

          <p class="ih-muted" *ngIf="selectedTransactions.length && selectedTransactions.length < selectedTransactionTotal">
            Showing the first {{ selectedTransactions.length }} of {{ selectedTransactionTotal }}.
          </p>
        </div>

        <div class="ih-dropped" *ngIf="selectedDropped.length">
          <h4>Rows that were not imported</h4>
          <div class="ih-dropped-row" *ngFor="let row of selectedDropped">
            <span class="ih-dropped-main">
              <span class="ih-dropped-desc">{{ row.description }}</span>
              <span class="ih-dropped-meta">{{ row.transactionDate }} &middot; {{ dropReason(row) }}</span>
            </span>
            <span class="ih-dropped-amount">{{ row.amount | number: '1.2-2' }}</span>
          </div>
          <p class="ih-muted" *ngIf="selectedDropped.length < (run.duplicateCount + run.unmappedCount)">
            Showing the first {{ selectedDropped.length }}.
          </p>
        </div>
      </div>
    </li>
  </ul>

  <div class="ih-pager" *ngIf="total > pageSize">
    <span class="ih-pager-range">{{ rangeLabel }}</span>
    <div class="ih-pager-buttons">
      <button type="button" class="ih-pager-btn" (click)="goToPage(page - 1)"
              [disabled]="page <= 1 || loading" aria-label="Previous page">&lsaquo;</button>
      <button type="button" class="ih-pager-btn" (click)="goToPage(page + 1)"
              [disabled]="page >= pageCount || loading" aria-label="Next page">&rsaquo;</button>
    </div>
  </div>
</div>
